export interface AnsExportOpts {
  include: {
    background: boolean;
    border: boolean;
    caption: { name?: string; tagline?: string } | null;
  };
  format: 'truecolor' | 'xterm256';
  scale: 1 | 2;
  centerIn?: number;
  targetCols?: number;
}

export function renderAns(
  raster: ImageData,
  opts: AnsExportOpts,
  extras: { backgroundColor: string; borderColor?: string },
): string {
  const { include, format, scale, centerIn, targetCols } = opts;

  let data: Uint8ClampedArray = new Uint8ClampedArray(raster.data);
  let w = raster.width;
  let h = raster.height;

  ({ data, w, h } = cropToContent(data, w, h));

  if (include.background) {
    data = compositeBackground(data, w, h, extras.backgroundColor);
  }

  if (targetCols && targetCols !== w) {
    const nh = Math.ceil(h * targetCols / w / 2) * 2;
    ({ data, w, h } = rescale(data, w, h, targetCols, nh));
  } else if (scale === 2) {
    ({ data, w, h } = upscale2x(data, w, h));
  }

  const leftPad = centerIn ? Math.max(0, Math.floor((centerIn - w) / 2)) : 0;
  const pad = leftPad > 0 ? '\x1b[0m' + ' '.repeat(leftPad) : '';

  let out = '';
  if (include.caption) {
    if (include.caption.name) out += `\x1b[1m${include.caption.name}\x1b[22m\n`;
    if (include.caption.tagline) out += `\x1b[2m${include.caption.tagline}\x1b[22m\n`;
  }

  const encoder = format === 'truecolor' ? encodeRowTruecolor : encodeRow256;
  const textRows = Math.floor(h / 2);
  for (let row = 0; row < textRows; row++) {
    out += pad + encoder(data, w, row);
    out += '\x1b[0m\n';
  }

  out += '\x1b[0m';
  return out;
}

function compositeBackground(data: Uint8ClampedArray, w: number, h: number, bgHex: string): Uint8ClampedArray {
  const br = parseInt(bgHex.slice(1, 3), 16);
  const bg = parseInt(bgHex.slice(3, 5), 16);
  const bb = parseInt(bgHex.slice(5, 7), 16);
  const out = new Uint8ClampedArray(data);
  for (let i = 0; i < out.length; i += 4) {
    if (out[i + 3] === 0) { out[i] = br; out[i+1] = bg; out[i+2] = bb; out[i+3] = 255; }
  }
  return out;
}

function cropToContent(data: Uint8ClampedArray, w: number, h: number): { data: Uint8ClampedArray; w: number; h: number } {
  let minX = w, maxX = -1, minY = h, maxY = -1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (data[(y * w + x) * 4 + 3] > 0) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return { data, w, h };
  // expand minY/maxY to even row boundaries for clean half-blocks
  minY = Math.floor(minY / 2) * 2;
  maxY = Math.ceil((maxY + 1) / 2) * 2 - 1;
  const nw = maxX - minX + 1, nh = maxY - minY + 1;
  const out = new Uint8ClampedArray(nw * nh * 4);
  for (let y = 0; y < nh; y++) for (let x = 0; x < nw; x++) {
    const src = ((y + minY) * w + (x + minX)) * 4;
    const dst = (y * nw + x) * 4;
    out[dst] = data[src]; out[dst+1] = data[src+1]; out[dst+2] = data[src+2]; out[dst+3] = data[src+3];
  }
  return { data: out, w: nw, h: nh };
}

function upscale2x(data: Uint8ClampedArray, w: number, h: number): { data: Uint8ClampedArray; w: number; h: number } {
  const w2 = w * 2, h2 = h * 2;
  const out = new Uint8ClampedArray(w2 * h2 * 4);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const src = (y * w + x) * 4;
    for (let dy = 0; dy < 2; dy++) for (let dx = 0; dx < 2; dx++) {
      const dst = ((y * 2 + dy) * w2 + (x * 2 + dx)) * 4;
      out[dst] = data[src]; out[dst+1] = data[src+1]; out[dst+2] = data[src+2]; out[dst+3] = data[src+3];
    }
  }
  return { data: out, w: w2, h: h2 };
}

function rescale(data: Uint8ClampedArray, w: number, h: number, nw: number, nh: number): { data: Uint8ClampedArray; w: number; h: number } {
  const out = new Uint8ClampedArray(nw * nh * 4);
  for (let y = 0; y < nh; y++) for (let x = 0; x < nw; x++) {
    const sx = Math.floor(x * w / nw), sy = Math.floor(y * h / nh);
    const src = (sy * w + sx) * 4, dst = (y * nw + x) * 4;
    out[dst] = data[src]; out[dst+1] = data[src+1]; out[dst+2] = data[src+2]; out[dst+3] = data[src+3];
  }
  return { data: out, w: nw, h: nh };
}

function getPixel(data: Uint8ClampedArray, w: number, x: number, y: number): [number,number,number,number] {
  const i = (y * w + x) * 4;
  return [data[i], data[i+1], data[i+2], data[i+3]];
}

function encodeRowTruecolor(data: Uint8ClampedArray, w: number, row: number): string {
  let s = '';
  let colored = false;
  for (let x = 0; x < w; x++) {
    const [tr, tg, tb, ta] = getPixel(data, w, x, row * 2);
    const [br, bg, bb, ba] = getPixel(data, w, x, row * 2 + 1);
    const topOn = ta > 0, botOn = ba > 0;
    if (!topOn && !botOn) {
      if (colored) { s += '\x1b[0m'; colored = false; }
      s += ' ';
      continue;
    }
    if (topOn && botOn) {
      s += `\x1b[38;2;${tr};${tg};${tb};48;2;${br};${bg};${bb}m▀`;
      colored = true;
      continue;
    }
    if (topOn) {
      s += `\x1b[0m\x1b[38;2;${tr};${tg};${tb}m▀`;
      colored = true;
      continue;
    }
    s += `\x1b[0m\x1b[38;2;${br};${bg};${bb}m▄`;
    colored = true;
  }
  return s;
}

const CUBE = [0, 95, 135, 175, 215, 255];
function nearestCubeIdx(v: number): number {
  let best = 0, bestD = 256;
  for (let i = 0; i < 6; i++) { const d = Math.abs(CUBE[i] - v); if (d < bestD) { bestD = d; best = i; } }
  return best;
}
function sqd(a: number[], b: number[]): number { return (a[0]-b[0])**2 + (a[1]-b[1])**2 + (a[2]-b[2])**2; }
function rgbTo256(r: number, g: number, b: number): number {
  const ri = nearestCubeIdx(r), gi = nearestCubeIdx(g), bi = nearestCubeIdx(b);
  const cubeIdx = 16 + 36 * ri + 6 * gi + bi;
  const cubeRgb = [CUBE[ri], CUBE[gi], CUBE[bi]];
  const avg = ((r + g + b) / 3) | 0;
  const grayI = Math.max(0, Math.min(23, ((avg - 8) / 10) | 0));
  const grayV = 8 + grayI * 10;
  return sqd([r, g, b], [grayV, grayV, grayV]) < sqd([r, g, b], cubeRgb) ? 232 + grayI : cubeIdx;
}

function encodeRow256(data: Uint8ClampedArray, w: number, row: number): string {
  let s = '';
  let colored = false;
  for (let x = 0; x < w; x++) {
    const [tr, tg, tb, ta] = getPixel(data, w, x, row * 2);
    const [br, bg, bb, ba] = getPixel(data, w, x, row * 2 + 1);
    const topOn = ta > 0, botOn = ba > 0;
    if (!topOn && !botOn) {
      if (colored) { s += '\x1b[0m'; colored = false; }
      s += ' ';
      continue;
    }
    if (topOn && botOn) {
      s += `\x1b[38;5;${rgbTo256(tr,tg,tb)};48;5;${rgbTo256(br,bg,bb)}m▀`;
      colored = true;
      continue;
    }
    if (topOn) {
      s += `\x1b[0m\x1b[38;5;${rgbTo256(tr,tg,tb)}m▀`;
      colored = true;
      continue;
    }
    s += `\x1b[0m\x1b[38;5;${rgbTo256(br,bg,bb)}m▄`;
    colored = true;
  }
  return s;
}
